import React, { useState, useEffect } from "react";
import { usePaystackPayment } from 'react-paystack';
import tw from "twin.macro";
import styled from "styled-components";
import { getAuth } from "firebase/auth";
import { addTransaction } from "services/backendCalls";
import { clearCartFromLocalStorage } from "services/reducer";
import cogoToast from "cogo-toast";
import Currency from "react-currency-formatter";
import useServedLocations from "hooks/useServedLocations";

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const CreditCardIcon = ({ className = "" }) => (
  <svg className={className} width="28" height="28" style={{ display: 'inline-block', width: '28px', height: '28px' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const CashIcon = ({ className = "" }) => (
  <svg className={className} width="28" height="28" style={{ display: 'inline-block', width: '28px', height: '28px' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const CheckoutContainer = tw.div`mt-4 flex flex-col`;
const CheckoutTitle = tw.h3`text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100`;
const FormGroup = tw.div`mb-4`;
const Label = tw.label`block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2`;
const Input = tw.input`w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 focus:shadow-outline transition-all text-sm font-medium text-gray-800 placeholder-gray-400 bg-white`;
const Select = styled.select`
  ${tw`w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 focus:shadow-lg transition-all text-sm font-medium text-gray-800 bg-white appearance-none cursor-pointer`}
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3E%3Cpath stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem 1.25rem;
  padding-right: 2.5rem;
`;
const PayButton = styled.button`
  ${tw`w-full mt-6 py-4 px-6 text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed shadow-lg active:scale-95`}
  ${({ pod }) => pod
    ? tw`bg-teal-700 hover:bg-teal-800 shadow-lg`
    : tw`bg-teal-600 hover:bg-teal-700 shadow-lg`}
`;
const SummarySection = tw.div`bg-teal-600 border border-teal-700 rounded-2xl p-5 mb-6`;
const SummaryRow = tw.div`flex justify-between items-center py-2 border-b border-teal-500 last:border-0`;
const SummaryLabel = tw.span`text-sm text-teal-100 font-medium`;
const SummaryValue = tw.span`text-sm font-semibold text-white`;
const TotalRow = tw(SummaryRow)`border-t border-teal-500 pt-3 mt-2`;
const TotalLabel = tw(SummaryLabel)`text-base font-bold text-white`;
const TotalValue = tw(SummaryValue)`text-xl font-extrabold text-white`;
const ErrorText = tw.p`text-red-500 text-xs font-semibold mt-1`;

const PaymentMethodSection = tw.div`mb-6`;
const PaymentMethodTitle = tw.p`text-xs font-bold text-gray-500 uppercase tracking-wider mb-3`;
const PaymentMethodGrid = tw.div`grid grid-cols-2 gap-3`;
const PaymentMethodBtn = styled.button`
  ${tw`flex flex-col items-center gap-2 p-4 border-2 rounded-2xl text-sm font-semibold transition-all duration-300 hover:scale-105 active:scale-95`}
  ${({ selected }) => selected
    ? tw`border-teal-600 bg-teal-100 text-teal-900 shadow-md`
    : tw`border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-500`}
`;


const DEFAULT_VAT_RATE = 0.075; // fallback if not set in Firestore

const CheckoutPaystack = ({ items = [], discountRate = 0, appliedCode = "", onSuccess: onSuccessCallback }) => {
  const auth = getAuth();
  const user = auth.currentUser;

  const [formData, setFormData] = useState({
    email: user?.email || "",
    firstname: user?.displayName?.split(' ')[0] || "",
    lastname: user?.displayName?.split(' ')[1] || "",
    phone: "",
    address: ""
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paystack'); // 'paystack' | 'pod'
  const [vatRate, setVatRate] = useState(DEFAULT_VAT_RATE);

  // Admin-managed served states & their neighborhoods (state → neighborhoods)
  const { states, neighborhoodsByState, allNeighborhoods } = useServedLocations();
  const [selectedState, setSelectedState] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");

  const selectedNeighborhoods = selectedState ? (neighborhoodsByState[selectedState] || []) : [];
  const neighborhoodOptions = selectedNeighborhoods.length > 0 ? selectedNeighborhoods : allNeighborhoods;

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedNeighborhood("");
  };

  // Calculate totals
  const totalItems = items.reduce((total, item) => Number(total) + Number(item.quantity || 1), 0);
  const subtotal = items.reduce((total, item) => Number(total) + Number(item.total || item.price || 0), 0);
  const discount = subtotal * discountRate;
  const tax = (subtotal - discount) * vatRate;
  const total = subtotal - discount + tax;

  // Fetch VAT from Firestore app_settings
  useEffect(() => {
    const fetchVat = async () => {
      try {
        const { fetchAppSettings } = await import("DBServices/firestoreDBService");
        const settings = await fetchAppSettings();
        const vatSetting = settings.find(s => s.key === 'vat_percentage');
        if (vatSetting && !isNaN(Number(vatSetting.value))) {
          setVatRate(Number(vatSetting.value) / 100);
        }
      } catch (e) {
        console.log("Could not fetch VAT setting, using default:", e);
      }
    };
    fetchVat();
  }, []);

  // Get Paystack key from environment
  const getPaystackKey = () => {
    try {
      const useLive = process.env.REACT_APP_USE_LIVE_PAYSTACK === 'true';
      return useLive
        ? process.env.REACT_APP_PAYSTACK_LIVE_KEY
        : process.env.REACT_APP_PAYSTACK_TEST_KEY;
    } catch (error) {
      return 'pk_test_48e5da1bf002032842197226fcb852bbcb535eb7';
    }
  };

  const publicKey = getPaystackKey();

  // Fetch user data from Firestore if available
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const { fetchUserDataFromFirestore } = await import("services/backendCalls");
          if (fetchUserDataFromFirestore) {
            const userData = await fetchUserDataFromFirestore(user.uid);
            if (userData) {
              setFormData(prev => ({
                ...prev,
                firstname: userData.FirstName || prev.firstname,
                lastname: userData.LastName || prev.lastname,
                phone: userData.Phone || prev.phone,
                email: userData.Email || prev.email
              }));
            }
          }
        } catch (error) {
          console.log("Could not fetch user data:", error);
        }
      }
    };
    fetchUserData();
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.firstname || formData.firstname.length < 2) {
      newErrors.firstname = "First name is required";
    }
    if (!formData.lastname || formData.lastname.length < 2) {
      newErrors.lastname = "Last name is required";
    }
    if (!formData.phone || formData.phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.address || formData.address.length < 5) {
      newErrors.address = "Delivery address is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const buildTransactionData = (reference, status, extraFields = {}) => {
    const data = {
      reference,
      status,
      userId: user?.uid || "guest",
      userEmail: formData.email,
      userName: `${formData.firstname} ${formData.lastname}`,
      userPhone: formData.phone,
      deliveryAddress: formData.address || '',
      state: selectedState || '',
      neighborhood: selectedNeighborhood || '',
      items: items.map(item => ({
        title: item.title || "Unknown Item",
        quantity: item.quantity || 1,
        price: item.price || 0,
        total: item.total || item.price || 0,
        imageSrc: item.imageSrc || "",
        basket: item.basket || []
      })),
      subtotal: subtotal || 0,
      discount: discount || 0,
      discountRate: (discountRate * 100).toFixed(1),
      discountCode: appliedCode || "",
      tax: tax || 0,
      total: total || 0,
      vatRate: (vatRate * 100).toFixed(1),
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      ...extraFields
    };
    Object.keys(data).forEach(key => { if (data[key] === undefined) delete data[key]; });
    return data;
  };

  // ── Paystack success handler ──────────────────────────────────────────────
  const onSuccess = (reference) => {
    const saveTransaction = async () => {
      try {
        const transactionData = buildTransactionData(
          reference.reference || `TXN_${Date.now()}`,
          reference.status || "success",
          {
            message: reference.message || "Payment successful",
            transaction: reference.transaction || reference.trxref || reference.reference,
            paymentMethod: 'paystack'
          }
        );
        await addTransaction(transactionData);
        clearCartFromLocalStorage();
        cogoToast.success("Payment successful! Thank you for your purchase.");
      } catch (error) {
        console.error("Error saving transaction:", error);
        cogoToast.success("Payment successful!");
      }
      if (onSuccessCallback) onSuccessCallback(reference);
    };
    saveTransaction();
  };

  const onClose = () => {
    cogoToast.info("Payment cancelled. Your items are still in the cart.");
  };

  const config = {
    reference: `TXN_${Date.now()}`,
    email: formData.email,
    amount: Math.round(total * 100),
    publicKey,
    metadata: {
      custom_fields: [
        { display_name: "Customer Name", variable_name: "customer_name", value: `${formData.firstname} ${formData.lastname}` },
        { display_name: "Phone Number", variable_name: "phone_number", value: formData.phone }
      ]
    }
  };

  const initializePayment = usePaystackPayment(config);

  // ── Pay on Delivery handler ───────────────────────────────────────────────
  const handlePODPayment = async () => {
    setIsProcessing(true);
    try {
      const reference = `POD_${Date.now()}`;
      const transactionData = buildTransactionData(reference, 'pay_on_delivery_pending', {
        paymentMethod: 'pay_on_delivery',
        message: 'Cash payment to be collected on delivery'
      });
      await addTransaction(transactionData);
      clearCartFromLocalStorage();
      cogoToast.success("Order placed! Pay on delivery when your items arrive.");
      if (onSuccessCallback) onSuccessCallback({ reference, status: 'pay_on_delivery_pending' });
    } catch (error) {
      cogoToast.error("Failed to place order. Please try again.");
      console.error("POD error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayment = () => {
    if (!validateForm()) {
      cogoToast.error("Please fill in all required fields correctly");
      return;
    }
    if (items.length === 0) {
      cogoToast.error("Your cart is empty");
      return;
    }

    if (paymentMethod === 'pod') {
      handlePODPayment();
      return;
    }

    setIsProcessing(true);
    try {
      initializePayment(onSuccess, onClose);
    } catch (error) {
      cogoToast.error("Failed to initialize payment. Please try again.");
      console.error("Payment initialization error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CheckoutContainer>
      <CheckoutTitle>Checkout</CheckoutTitle>

      <SummarySection>
        <SummaryRow>
          <SummaryLabel>Items ({totalItems})</SummaryLabel>
          <SummaryValue><Currency quantity={subtotal} currency="NGN" /></SummaryValue>
        </SummaryRow>
        {discountRate > 0 && (
          <SummaryRow>
            <SummaryLabel>Discount</SummaryLabel>
            <SummaryValue>−<Currency quantity={discount} currency="NGN" /></SummaryValue>
          </SummaryRow>
        )}
        <SummaryRow>
          <SummaryLabel>VAT ({(vatRate * 100).toFixed(1)}%)</SummaryLabel>
          <SummaryValue><Currency quantity={tax} currency="NGN" /></SummaryValue>
        </SummaryRow>
        <TotalRow>
          <TotalLabel>Total</TotalLabel>
          <TotalValue><Currency quantity={total} currency="NGN" /></TotalValue>
        </TotalRow>
      </SummarySection>

      {/* ── Payment Method Toggle ─────────────────────────────── */}
      <PaymentMethodSection>
        <PaymentMethodTitle>Select Payment Method</PaymentMethodTitle>
        <PaymentMethodGrid>
          <PaymentMethodBtn
            type="button"
            selected={paymentMethod === 'paystack'}
            onClick={() => setPaymentMethod('paystack')}
          >
            <CreditCardIcon className={`w-7 h-7 transition-colors duration-300 ${paymentMethod === 'paystack' ? 'text-teal-600' : 'text-gray-400'}`} />
            <span>Pay Online</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 400 }}>Paystack (Card / Transfer)</span>
          </PaymentMethodBtn>
          <PaymentMethodBtn
            type="button"
            selected={paymentMethod === 'pod'}
            onClick={() => setPaymentMethod('pod')}
          >
            <CashIcon className={`w-7 h-7 transition-colors duration-300 ${paymentMethod === 'pod' ? 'text-teal-600' : 'text-gray-400'}`} />
            <span>Pay on Delivery</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 400 }}>Cash when delivered</span>
          </PaymentMethodBtn>
        </PaymentMethodGrid>
      </PaymentMethodSection>
      <FormGroup>
        <Label htmlFor="email">Email Address *</Label>
        <Input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="your.email@example.com" />
        {errors.email && <ErrorText>{errors.email}</ErrorText>}
      </FormGroup>

      <div className="grid grid-cols-2 gap-4">
        <FormGroup>
          <Label htmlFor="firstname">First Name *</Label>
          <Input type="text" id="firstname" name="firstname" value={formData.firstname} onChange={handleInputChange} placeholder="John" />
          {errors.firstname && <ErrorText>{errors.firstname}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastname">Last Name *</Label>
          <Input type="text" id="lastname" name="lastname" value={formData.lastname} onChange={handleInputChange} placeholder="Doe" />
          {errors.lastname && <ErrorText>{errors.lastname}</ErrorText>}
        </FormGroup>
      </div>

      <FormGroup>
        <Label htmlFor="phone">Phone Number *</Label>
        <Input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="08012345678" />
        {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
      </FormGroup>

      <div className="grid grid-cols-2 gap-4">
        <FormGroup>
          <Label htmlFor="state">State *</Label>
          <Select id="state" name="state" value={selectedState} onChange={handleStateChange}>
            <option value="">Select state</option>
            {states.map((s) => (
              <option key={s.firestoreId || s.name} value={s.name}>{s.name}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="neighborhood">Neighborhood *</Label>
          <Select
            id="neighborhood"
            name="neighborhood"
            value={selectedNeighborhood}
            onChange={(e) => setSelectedNeighborhood(e.target.value)}
            disabled={!selectedState}
          >
            <option value="">
              {!selectedState
                ? "Select State"
                : neighborhoodOptions.length === 0
                ? "None found"
                : "Select area"}
            </option>
            {neighborhoodOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
        </FormGroup>
      </div>

      <FormGroup>
        <Label htmlFor="address">Delivery Address *</Label>
        <Input type="text" id="address" name="address" value={formData.address} onChange={handleInputChange} placeholder="15 Awolowo Road, Lagos" />
        {errors.address && <ErrorText>{errors.address}</ErrorText>}
      </FormGroup>

      <PayButton
        pod={paymentMethod === 'pod' ? 1 : 0}
        onClick={handlePayment}
        disabled={isProcessing || items.length === 0}
      >
        {isProcessing ? (
          <>
            <span className="spinner-border spinner-border-sm mr-2 animate-spin"></span>
            Processing...
          </>
        ) : paymentMethod === 'pod' ? (
          <>
            <svg width="20" height="20" style={{ width: '20px', height: '20px' }} className="mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span>Place Order (Pay on Delivery)</span>
          </>
        ) : (
          <>
            <svg width="20" height="20" style={{ width: '20px', height: '20px' }} className="mr-2" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Pay <Currency quantity={total} currency="NGN" /></span>
          </>
        )}
      </PayButton>
    </CheckoutContainer>
  );
};

export default CheckoutPaystack;
